'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Image } from '@/components/ui/Image';
import type { Listing, ListingImage } from '@repo/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui';
import Carousel from '@/components/carousel/Carousel';
import { CarouselCounter } from '@/components/carousel';
import { useDeviceType } from '@/hooks/useDeviceType';
import { useDeferredReady } from '@/hooks/useDeferredReady';
import type { DeviceType } from '@/lib/device-type';

const FullScreenCarousel = dynamic(
  () => import('@/components/carousel').then(m => m.FullScreenCarousel),
  { ssr: false },
);

interface UnitImageCarouselProps {
  listing: Listing;
  images: ListingImage[];
  initialDeviceType?: DeviceType;
}

export function UnitImageCarousel({
  listing,
  images,
  initialDeviceType = 'desktop',
}: UnitImageCarouselProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shouldLoadFullscreen, setShouldLoadFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const { isMobile } = useDeviceType(initialDeviceType);
  const isDeferredReady = useDeferredReady({
    skip: false,
    delayMs: 1500,
    scope: 'unit-image-carousel',
  });

  // Use placeholder image if no images are available
  const displayImages =
    !images || images.length === 0
      ? [
          {
            url: '/listing-card-placeholder.png',
            imageM: '/listing-card-placeholder.png',
            imageS: '/listing-card-placeholder.png',
            imageL: '/listing-card-placeholder.png',
            imageDescription: `${listing.fullAddress} - No images available`,
          } as ListingImage,
        ]
      : images;

  const mainImage = displayImages[0];
  if (!mainImage) return null;

  const showAllMobileImages = isDeferredReady || displayImages.length <= 1;
  const visibleMobileImages = showAllMobileImages ? displayImages : displayImages.slice(0, 1);

  const thumbnailImages = displayImages.slice(1, 5); // Next 4 thumbnails only

  const handleImageClick = (index: number) => {
    if (images.length === 0) return;
    setShouldLoadFullscreen(true);
    setFullscreenIndex(index);
    setIsFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  const fullscreenCarouselId = `unit-images-fullscreen-${listing.id}`;
  const title = listing.fullAddress;

  return (
    <>
      {/* Mobile: Swipeable Carousel (deferred until idle) */}
      {isMobile ? (
        <div className="relative w-full">
          <div className="relative w-full">
            <Carousel
              carouselId="unit-images-mobile"
              items={visibleMobileImages.map((img, index) => ({
                id: `unit-images-mobile-item-${index}`,
                content: (
                  <button
                    onClick={() => handleImageClick(index)}
                    className={cn(
                      'w-full h-full relative block',
                      images.length === 0 ? '!cursor-auto' : 'cursor-pointer group',
                    )}
                    style={{ aspectRatio: '4:3' }}
                  >
                    <Image
                      aspectRatio="4:3"
                      quality={75}
                      src={img.imageS || img.imageM || img.url || ''}
                      alt={img.imageDescription || `${listing.fullAddress} - Image ${index + 1}`}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      sizes="100vw"
                      className="w-full h-full object-cover"
                    />
                    {images.length > 0 && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    )}
                  </button>
                ),
              }))}
              perView={{ mobile: 1 }}
              shouldFreeScroll={false}
              gap={0}
              ariaLabel="Unit images"
            />

            {displayImages.length > 1 && (
              <CarouselCounter
                carouselId="unit-images-mobile"
                totalSlides={displayImages.length}
                className="absolute bottom-4 right-4 z-10"
              />
            )}
          </div>
        </div>
      ) : (
        <div
          className="px-4 flex relative bg-white flex-row rounded-2xl overflow-hidden w-full"
          style={{ height: '465px' }}
        >
          {/* Main Image: Left half */}
          <div className={cn('h-full', displayImages.length === 1 ? 'w-full' : 'w-3/5')}>
            <button
              onClick={() => handleImageClick(0)}
              className={cn(
                'w-full h-full relative block',
                images.length === 0 ? '!cursor-auto' : 'cursor-pointer group',
              )}
            >
              <Image
                quality={75}
                src={mainImage.imageS || mainImage.imageM || mainImage.url || ''}
                alt={mainImage.imageDescription || `${listing.fullAddress} - Main image`}
                sizes={displayImages.length === 1 ? '100vw' : '(max-width: 768px) 100vw, 60vw'}
                fetchPriority="high"
                loading="eager"
                className="w-full h-full rounded-2xl object-cover"
              />
              {images.length > 0 && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-2xl" />
              )}
            </button>
          </div>

          {/* Thumbnails: Right half */}
          {displayImages.length > 1 && (
            <div
              className={cn(
                'w-2/5 h-full grid gap-2 ml-2',
                displayImages.length === 2 && 'grid-cols-1 grid-rows-1',
                displayImages.length === 3 && 'grid-cols-1 grid-rows-2',
                displayImages.length >= 4 && 'grid-cols-2 grid-rows-2',
              )}
            >
              {thumbnailImages.map((img, index) => {
                const actualIndex = index + 1;

                // Determine col-span for specific layouts
                // Case 4 images: First thumbnail (index 0) spans 2 columns
                const isFullWidth = displayImages.length === 4 && index === 0;

                return (
                  <button
                    key={actualIndex}
                    onClick={e => {
                      e.stopPropagation();
                      handleImageClick(actualIndex);
                    }}
                    className={cn(
                      'relative w-full h-full overflow-hidden rounded-xl',
                      images.length === 0 ? '!cursor-auto' : 'cursor-pointer group',
                      isFullWidth && 'col-span-2',
                    )}
                  >
                    <Image
                      quality={50}
                      src={img.imageM || img.imageS || img.url || ''}
                      alt={
                        img.imageDescription || `${listing.fullAddress} - Image ${actualIndex + 1}`
                      }
                      loading="lazy"
                      fetchPriority="low"
                      sizes={
                        displayImages.length === 2
                          ? '40vw'
                          : displayImages.length === 3
                            ? '20vw'
                            : isFullWidth
                              ? '40vw'
                              : '20vw'
                      }
                      className="w-full h-full"
                    />
                    {images.length > 0 && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl z-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-4 right-8 z-10">
              <Button
                variant="secondary"
                size="sm"
                onClick={e => {
                  e.stopPropagation();
                  handleImageClick(0);
                }}
              >
                View all {displayImages.length} photos
              </Button>
            </div>
          )}
        </div>
      )}

      {shouldLoadFullscreen ? (
        <FullScreenCarousel
          open={isFullscreen}
          carouselId={fullscreenCarouselId}
          title={title}
          initialIndex={fullscreenIndex}
          onClose={handleCloseFullscreen}
          items={displayImages.map((img, index) => ({
            id: `${fullscreenCarouselId}-${index}`,
            src: img.imageL || img.imageM || img.imageS || img.url || '',
            alt: img.imageDescription || `${listing.fullAddress} - Image ${index + 1}`,
          }))}
        />
      ) : null}
    </>
  );
}
